import axios, { AxiosResponse } from 'axios';
import store from 'app/store';
import qs from 'qs';
import { MIN_API_USER_TOKEN_VERSION } from 'consts';
import { clearAuthDetails, setAuthDetails } from 'views/Auth/authSlice';
import { currencyType } from 'data/currencies';

export const API_URL = process.env.REACT_APP_API_URL;
// env will be set to something like https://stage.tsbdev.co/api/v1

// Does not throw error on req failure
axios.defaults.validateStatus = function () {
  return true;
};

axios.interceptors.response.use(async function (response) {
  if (response.status === 401) {
    // Add more 401 exceptions here

    if (response.request.responseURL.includes('login-otp') || response.request.responseURL.includes('login-password'))
      return response;
    const userType = await store.getState().auth.userType;
    await store.dispatch(clearAuthDetails());
    if (userType === 'CLIENT') {
      window.location.href = '/client';
    } else if (userType === 'ADMIN') {
      window.location.href = '/admin';
    } else if (userType === 'USER' && window.location.href.includes('dashboard')) {
      window.location.href = '/';
    } else {
      window.location.reload();
    }
  }
  return response;
});

enum REST_METHODS {
  GET = 'get',
  POST = 'post',
  DELETE = 'delete',
  PATCH = 'patch',
}

interface Dates {
  from?: Date;
  to?: Date;
}
interface PaginationParams extends Dates {
  pageNumber?: number;
  pageSize?: number;
}

interface ClientPaymentStats extends Dates {
  promotional?: string;
}

interface AggregatedConsumptionParams extends Dates {
  columns?: string[];
  slice?: string;
  clientId?: string;
}

interface CouponParams {
  activatesAt?: Date;
  deactivatesAt?: Date;
  code?: string;
  visibility?: string;
  maxValue?: number;
  clients?: string[] | null;
  contents?: string[] | null;
  newUsersOnly?: boolean;
  couponId?: string;
  reusable?: boolean;
  countInRevenue?: boolean;
}

interface TotalUsersParams extends Dates {
  slice?: string;
}

interface TopStoriesParams extends PaginationParams {
  sortBy: string;
}

interface transactionWithPagination extends Dates {
  status?: string;
  skip?: number;
  id?: string;
  category?: string;
  currency: string;
  phoneNumberOrEmail?: string;
  download?: boolean;
  clientId?: string;
}

interface SubscriptionStatsParams extends PaginationParams {
  clientId?: string;
}

interface ConcurrentReadersParams extends Dates {
  slice?: string;
  clientId?: string;
}

interface BifurcatedPricesParams extends Dates {
  clientId?: string;
}

interface GeoLocationParams extends Dates {
  clientId?: string;
  purchaseType?: string;
}

interface UTMParams extends Dates {
  clientId?: string;
  utmParameter?: string;
  contentIds?: string[];
  subscriptionIds?: string[];
  onlySubscriptions?: boolean;
  purchasesOnly?: boolean;
  utmValue?: string;
}

interface TotalWalletAdditionParams extends Dates {
  slice?: string;
}

function paginationArgsToParams({ from, to, pageNumber, pageSize }: PaginationParams) {
  return {
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    pageNumber,
    pageSize,
  };
}

interface paginationWithClientParams extends Dates {
  pageNumber?: number;
  pageSize?: number;
  clientId?: string;
  contentIds?: string[];
  download?: boolean;
  type?: string;
}

const apiCaller = async (
  {
    method,
    params,
    url,
    data,
    headers,
    excludeAuthToken,
  }: {
    method: REST_METHODS;
    params?: Record<string, unknown>;
    url: string;
    data?: Record<string, unknown> | FormData;
    headers?: Record<string, unknown>;
    excludeAuthToken?: boolean;
  } = {
    method: REST_METHODS.GET,
    params: {},
    data: {},
    headers: {},
    url: '',
    excludeAuthToken: false,
  },
) => {
  let authDetails = await store.getState().auth;
  let userToken = authDetails.ereader_token || authDetails.token;
  const userType = authDetails.userType;
  const authExpiry = authDetails.expiresAt;
  const tokenVersion = authDetails.version;
  if (userType === 'USER' && authDetails.expiresAt) {
    if (new Date().getTime() > new Date(authExpiry).getTime() || tokenVersion < MIN_API_USER_TOKEN_VERSION) {
      const newAuthDetailsResponse = await axios({
        method: 'POST',
        url: `${API_URL}/auth/renew-token`,
        headers: { ...headers, Authorization: `Bearer ${userToken}` },
      });

      if (newAuthDetailsResponse.status !== 401) {
        await store.dispatch(setAuthDetails({ ...newAuthDetailsResponse.data, userType: 'USER' }));
        authDetails = await store.getState().auth;
        userToken = authDetails.token;
      } else {
        await store.dispatch(clearAuthDetails());
      }
    }
  }

  if (userToken && !excludeAuthToken) {
    headers = { ...headers, Authorization: `Bearer ${userToken}` };
  }

  if (method === REST_METHODS.GET) {
    return axios.get(url, { params, headers });
  }
  return axios({
    method: method,
    url,
    data,
    headers,
    params,
  });
};

// !CONVENTION: All api methods start with a capital letter so as to not confuse with nesting
const API = {
  validators: {
    checkSuccessCode: (response: AxiosResponse): boolean => {
      return Math.floor(response.status / 100) === 2;
    },
    matchStatusCode: (response: AxiosResponse, statusCode: number): boolean => {
      return response.status === statusCode ? true : false;
    },
  },
  auth: {
    CreateAccount: ({
      phoneNumber,
      email,
      clientId,
      utmParameters,
      eventLocation,
      anonId,
      paywallId,
      landingPageId,
      tierId,
    }: {
      phoneNumber?: string;
      email?: string;
      clientId?: string;
      utmParameters?: Record<string, any>;
      eventLocation?: string;
      anonId?: string;
      paywallId?: string;
      landingPageId?: string;
      tierId?: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/auth/create-account`,
        data: { phoneNumber, email, clientId, utmParameters, eventLocation, anonId, paywallId, landingPageId, tierId },
      });
    },
    SendVerificationEmail: ({
      email,
      clientId,
      eventLocation,
      anonId,
      paywallId,
    }: {
      email: string;
      clientId?: string;
      eventLocation?: string;
      anonId?: string;
      paywallId?: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/auth/send-verification-email`,
        data: { email, clientId, eventLocation, anonId, paywallId },
      });
    },
    AutoLogin: ({
      token,
      clientId,
      email,
      phoneNumber,
      loginChallengeId,
    }: {
      token: string;
      clientId: string;
      email?: string;
      phoneNumber?: string;
      loginChallengeId?: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/auth/login-using-temp-token`,
        data: {
          ...(email && { email }),
          ...(loginChallengeId && { loginChallengeId }),
          clientId,
          tempAuthToken: token,
          ...(phoneNumber && { phoneNumber }),
        },
      });
    },
    GoogleLogin: ({
      token,
      sessionId,
      clientId,
      clientTierId,
    }: {
      token: string;
      sessionId?: string;
      clientId?: string;
      clientTierId?: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/auth/google-login`,
        data: { token, sessionId, clientId, clientTierId },
      });
    },
    SendOtp: ({
      phoneNumber,
      clientId,
      isClientLoginFlow,
      eventLocation,
      anonId,
      _id,
      paywallId,
    }: {
      phoneNumber: string;
      clientId?: string;
      isClientLoginFlow?: boolean;
      eventLocation?: string;
      anonId?: string;
      _id?: string;
      paywallId?: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/auth/send-otp`,
        data: { phoneNumber, clientId, isClientLoginFlow, _id, eventLocation, anonId, paywallId },
      });
    },
    LoginOtp: ({
      OTP,
      phoneNumber,
      email,
      clientId,
      promotionalOptIn,
      clientTierId,
      freeTrial,
      _id,
      eventLocation,
      anonId,
      firstTimeLogin,
      paywallId,
      landingPageId,
      tierId,
    }: {
      OTP: string;
      phoneNumber?: string;
      email?: string;
      clientId?: string;
      promotionalOptIn?: boolean;
      clientTierId?: string;
      freeTrial?: boolean;
      _id?: string;
      eventLocation?: string;
      anonId?: string;
      firstTimeLogin?: boolean;
      paywallId?: string;
      landingPageId?: string;
      tierId?: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        data: {
          phoneNumber,
          email,
          OTP,
          clientId,
          promotionalOptIn,
          clientTierId,
          freeTrial,
          _id,
          eventLocation,
          anonId,
          firstTimeLogin,
          paywallId,
          landingPageId,
          tierId,
        },
        url: `${API_URL}/auth/login-otp`,
      });
    },
    LoginPassword: ({ phoneNumber, password }: { phoneNumber: string; password: string }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        data: { phoneNumber, password },
        url: `${API_URL}/auth/login-password`,
      });
    },
    Logout: () => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/auth/logout`,
      });
    },
    LogoutAllSessions: () => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/auth/logout-all-sessions`,
      });
    },
    ClientLogin: ({ email, password }: { email: string; password: string }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        data: { email, password },
        url: `${API_URL}/client/login`,
      });
    },
    AdminLogin: ({ email, password }: { email: string; password: string }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        data: { email, password },
        url: `${API_URL}/admin/login`,
      });
    },
    DetectCountry: () => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/auth/detect-country`,
      });
    },
    GetTierDetails: ({ tierId }: { tierId: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/tier`,
        params: {
          tierId,
        },
      });
    },
    AddEmailNumber: ({
      phoneNumber,
      email,
      clientId,
      _id,
    }: {
      phoneNumber?: string;
      clientId?: string;
      _id: string;
      email?: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/auth/add-email-number`,
        data: { phoneNumber, clientId, _id, email },
      });
    },
  },
  content: {
    convertCurrency: ({ from, to, amount }: { from: string; to: string; amount: number }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/content/convert-currency`,
        params: { from, to, amount },
      });
    },
    ContentDetails: ({
      clientContentId,
      clientId,
      sessionId,
      userDateTime,
      contentPurchaseType,
    }: {
      clientContentId: string;
      clientId: string;
      sessionId?: string;
      userDateTime?: Date;
      contentPurchaseType?: {
        digital: boolean;
        adFree: boolean;
      };
    }): Promise<AxiosResponse<unknown>> => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/content`,
        params: {
          clientContentId,
          clientId,
          ...(sessionId && { sessionId }),
          userDateTime,
          contentPurchaseType: JSON.stringify(contentPurchaseType),
        },
        headers: {
          href: window.location.href,
        },
        excludeAuthToken: !sessionId ? true : false,
      });
    },
    GetViewDetails: ({ viewId }: { viewId: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/content/view`,
        params: { viewId },
      });
    },

    CreateView: ({
      clientId,
      clientContentId,
      sessionId,
      subscription,
      utmParameters,
      visitId,
      visitorId,
    }: {
      clientId: string;
      clientContentId: string;
      sessionId?: string;
      subscription?: boolean;
      utmParameters?: Record<string, any>;
      visitId?: string;
      visitorId?: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/content/view`,
        data: { clientId, clientContentId, subscription, sessionId, utmParameters, visitId, visitorId },
      });
    },
    UpdateView: ({
      viewId,
      action,
      userId,
      sessionId,
      paywallType,
      freeTrial,
      subscriptionDetails,
      paywallId,
    }: {
      viewId: string;
      action?: string;
      userId?: string;
      sessionId?: string;
      paywallType?: string;
      freeTrial?: boolean;
      paywallId?: string;
      subscriptionDetails?: Record<string, any>;
    }) => {
      return apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/content/view`,
        data: { viewId, action, userId, paywallId, sessionId, paywallType, freeTrial, subscriptionDetails },
      });
    },

    AddCoupon: ({
      activatesAt,
      deactivatesAt,
      code,
      visibility,
      maxValue,
      clients,
      contents,
      newUsersOnly,
      reusable,
      countInRevenue,
    }: CouponParams) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/content/coupon`,
        data: {
          activatesAt,
          deactivatesAt,
          code,
          visibility,
          maxValue,
          clients,
          contents,
          newUsersOnly,
          reusable,
          countInRevenue,
        },
      });
    },

    UpdateCoupon: ({
      activatesAt,
      deactivatesAt,
      couponId,
      visibility,
      maxValue,
      clients,
      contents,
      newUsersOnly,
      reusable,
      countInRevenue,
    }: CouponParams) => {
      return apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/content/coupon/${couponId}`,
        data: {
          activatesAt,
          deactivatesAt,
          visibility,
          maxValue,
          clients,
          contents,
          newUsersOnly,
          reusable,
          countInRevenue,
        },
      });
    },

    AddPriceCategory: ({
      name,
      price,
      duration,
      currency,
      priceOverrides,
    }: {
      name: string;
      price: number;
      duration: number;
      currency: string;
      priceOverrides?: {
        country: {
          name: string;
          price: number;
          currency: string;
        }[];
      };
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/content/price-category`,
        data: { name, price, duration, currency, priceOverrides },
      });
    },

    UpdatePriceCategory: ({
      name,
      price,
      duration,
      currency,
      priceOverrides,
    }: {
      name: string;
      price: number;
      duration: number;
      currency: string;
      priceOverrides?: {
        country: {
          name: string;
          price: number;
          currency: string;
        }[];
      };
    }) => {
      return apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/content/price-category`,
        data: { name, price, duration, currency, priceOverrides },
      });
    },

    DeletePriceCategory: ({ name }: { name: string }) => {
      return apiCaller({
        method: REST_METHODS.DELETE,
        url: `${API_URL}/content/price-category`,
        data: { name },
      });
    },

    FilterContentByTitle: ({ partialTitle, clientId }: { partialTitle?: string; clientId?: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/content/filter/title`,
        params: { partialTitle, clientId },
      });
    },

    FetchPriceCategory: ({ clientId }: { clientId?: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/content/price-category`,
        params: { clientId },
      });
    },

    AccessCheck: ({
      clientContentId,
      clientId,
      userId,
      sessionId,
      templateId,
    }: {
      clientContentId: string;
      clientId: string;
      userId?: string;
      sessionId?: string;
      templateId?: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/content/access`,
        data: { clientContentId, clientId, userId, sessionId, templateId },
      });
    },

    DownloadConsumption: ({ consumptionId }: { consumptionId: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/content/download/${consumptionId}`,
      });
    },
  },
  user: {
    AccountDetails: ({ email, phoneNumber }: { email?: string; phoneNumber?: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/user/info`,
        params: {
          email,
          phoneNumber,
        },
      });
    },
    UpdatePromotionalOptIn: ({ promotionalOptIn }: { promotionalOptIn: boolean }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/user/promotional-opt-in`,
        data: {
          promotionalOptIn,
        },
      });
    },
    setDetails: (data: {
      name?: string;
      secondaryEmail?: string;
      secondaryPhoneNumber?: string;
      dateOfBirth?: string;
      pincode?: string;
      address?: {
        apartment: string;
        area: string;
        city: string;
        state: string;
        country: string;
        landmark: string;
        pincode: string;
      };
      email?: string;
      phoneNumber?: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/user/account`,
        data,
      });
    },
    Feedback: ({ formData }: { formData: FormData }) => {
      return apiCaller({
        url: `${API_URL}/user/feedback`,
        method: REST_METHODS.POST,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      });
    },
    Details: () => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/user`,
      });
    },

    TemporaryUser: (referenceId?: string) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/user/temporary-user`,
        params: { referenceId },
      });
    },
    validateSession: (sessionId: string) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/user/validate-sessionId`,
        params: { sessionId },
      });
    },
    UpdateLoggedInClients: ({ clientId, clientTierId }: { clientId: string; clientTierId?: string }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/auth/update-logged-in-clients`,
        data: {
          clientId,
          clientTierId,
        },
      });
    },
    ContentDetails: ({
      clientContentId,
      clientId,
    }: {
      clientContentId: string;
      clientId: string;
    }): Promise<AxiosResponse<unknown>> => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/content/user`,
        params: { clientContentId, clientId },
      });
    },

    latestPurchaseDetails: ({ clientId }: { clientId: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/user/latest-subscription-details`,
        params: { clientId },
      });
    },

    AssociateAmpReaderId: ({ rid }: { rid: string }): Promise<AxiosResponse<{ message: string }>> => {
      return apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/user/amp`,
        data: { rid },
      });
    },
    Purchase: ({
      clientContentId,
      clientId,
      price,
      title,
      url,
      currency,
      utmParameters,
      paywallId,
      journeyId,
      templateId,
      couponCode,
      userDateTime,
      type,
      contentPurchaseType,
      popupId,
      eventLocation,
      anonId,
      firstTimeLogin,
    }: {
      clientContentId: string;
      clientId: string;
      price: number;
      title?: string;
      journeyId?: any;
      paywallId?: any;
      templateId?: any;
      url?: string;
      currency?: string;
      utmParameters?: Record<string, any>;
      couponCode?: string;
      userDateTime?: Date;
      type: string;
      contentPurchaseType?: { digital: boolean; adFree: boolean };
      popupId?: string;
      eventLocation?: string;
      anonId?: string;
      firstTimeLogin?: boolean;
    }): Promise<AxiosResponse<unknown>> => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/content/purchase`,
        data: {
          clientContentId,
          clientId,
          price,
          title,
          url,
          currency,
          paywallId,
          journeyId,
          templateId,
          utmParameters,
          couponCode,
          type,
          contentType: contentPurchaseType,
          userDateTime,
          purchaseLocation: eventLocation,
          anonId,
          firstTimeLogin,
          ...(popupId && { popupId }),
        },
      });
    },
    AuthorizeRenewal: ({
      price,
      currency,
      tierId,
      subscriptionId,
      duration,
      contentDetails,
    }: {
      price: number;
      currency: string;
      tierId: string;
      subscriptionId: string;
      duration: number;
      contentDetails?: any;
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/subscription/authorize-renewal`,
        data: {
          price,
          currency,
          tierId,
          subscriptionId,
          duration,
          contentDetails,
        },
      });
    },
    PurchaseSubscription: ({
      clientContentId,
      clientId,
      price,
      currency,
      utmParameters,
      offerIds,
      couponCode,
      tierId,
      subscriptionId,
      renew,
      transactionInfo,
      consumeFreeTrial,
      popupId,
      manuallyRenewed,
      rzpSubscriptionId,
      viewId,
    }: {
      clientContentId?: string;
      clientId: string;
      price?: number;
      currency?: string;
      utmParameters?: Record<string, any>;
      couponCode?: string;
      tierId: string;
      offerIds?: string[];
      subscriptionId: string;
      renew?: boolean;
      transactionInfo?: string;
      consumeFreeTrial?: boolean;
      popupId?: string;
      manuallyRenewed?: boolean;
      rzpSubscriptionId?: string;
      viewId?: string;
    }): Promise<AxiosResponse<unknown>> => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/subscription/purchase`,
        data: {
          clientContentId,
          clientId,
          price,
          currency,
          utmParameters,
          couponCode,
          tierId,
          offerIds,
          subscriptionId,
          renew,
          transactionInfo,
          consumeFreeTrial,
          ...(popupId && { popupId }),
          manuallyRenewed,
          rzpSubscriptionId,
          viewId,
        },
      });
    },
    PurchaseSubscriptionBundle: (referenceId: string): Promise<AxiosResponse<unknown>> => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/subscription/purchaseBundle`,
        data: { referenceId },
      });
    },
    CancelSubscription: ({ rzpSubscriptionId }: { rzpSubscriptionId: string }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/subscription/cancel`,
        data: {
          rzpSubscriptionId,
        },
      });
    },
    setPassword: ({ password }: { password: string }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/user/set-password`,
        data: {
          password,
        },
      });
    },
    setSecondaryDetails: ({
      secondaryEmail,
      secondaryPhoneNumber,
    }: {
      secondaryEmail?: string;
      secondaryPhoneNumber?: string;
    }): Promise<AxiosResponse<unknown>> => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/user/secondary-details`,
        data: { secondaryEmail, secondaryPhoneNumber },
      });
    },
    TotalMoneyAdded: ({ from, to }: Dates) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/user/money-added`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
        },
      });
    },
    UserSubscriptions: ({ from, to }: Dates) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/user/subscriptions`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
        },
      });
    },

    stats: {
      Consumption: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/user/stats/consumption`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      PassesConsumption: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/user/stats/passes-consumption`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      ActivePasses: () =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/user/active-passes`,
        }),
      DownloadConsumption: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/user/stats/download-consumption`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      TotalConsumption: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/user/stats/aggregated-consumption`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      Transactions: (paginationParams: PaginationParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/user/stats/transactions`,
          params: paginationArgsToParams(paginationParams),
        }),
      Donations: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/user/donations`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
    },
    payment: {
      createPayment: ({
        amount,
        contentDetails,
        currency,
        donationId,
      }: {
        amount: number;
        contentDetails?: any;
        currency: string;
        donationId?: string;
      }): Promise<AxiosResponse<unknown>> => {
        return apiCaller({
          method: REST_METHODS.POST,
          url: `${API_URL}/payment`,
          data: {
            amount,
            contentDetails,
            currency,
            donationId,
          },
        });
      },
      createRzpPayment: ({
        amount,
        currency,
        contentDetails,
        donationId,
      }: {
        amount: number;
        currency: string;
        contentDetails?: Record<string, any>;
        donationId?: string;
      }) => {
        return apiCaller({
          method: REST_METHODS.POST,
          url: `${API_URL}/payment/razorpay`,
          data: {
            amount,
            contentDetails,
            currency,
            donationId,
          },
        });
      },
      getTransaction: ({
        transactionId,
      }: {
        transactionId: string;
      }): Promise<
        AxiosResponse<{
          transaction: { contentDetails: Record<string, any>; rzpResponse: string; purchasedSubscription?: boolean };
        }>
      > => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/payment/transaction/${transactionId}`,
        });
      },
    },
    getLoginChallenge: (loginChallengeId: string): Promise<AxiosResponse<unknown>> => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/login-challenge/${loginChallengeId}`,
      });
    },
    updateLoginChallenge: (loginChallengeId: string, sessionId: string): Promise<AxiosResponse<unknown>> => {
      return apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/login-challenge/${loginChallengeId}`,
        data: { sessionId },
      });
    },
  },
  donation: {
    GetDonation: ({ donationId }: { donationId: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/donation`,
        params: {
          donationId,
        },
      });
    },
    UpdateDonation: ({
      donor,
      amount,
      currency,
      donationId,
    }: {
      donor: {
        name?: string;
        email?: string;
        message?: string;
      };
      amount: number;
      currency: string;
      donationId: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/donation/${donationId}`,
        data: {
          donor,
          amount,
          currency,
        },
      });
    },
    ConfirmDonation: ({ donationId }: { donationId: string }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/donation/confirm/${donationId}`,
      });
    },
  },
  client: {
    SkipGoals: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/goal/skip-updating-goal-this-month`,
      }),

    UpdateGoal: ({
      userGoal,
      payerGoal,
      revenueGoalAmount,
      revenueGoalCurrency,
    }: {
      userGoal: number;
      payerGoal: number;
      revenueGoalAmount: number;
      revenueGoalCurrency: string;
    }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/client/goal`,
        data: {
          userGoal,
          payerGoal,
          revenueGoalAmount,
          revenueGoalCurrency,
        },
      }),

    CreateGoal: ({
      userGoal,
      payerGoal,
      revenueGoalAmount,
      revenueGoalCurrency,
    }: {
      userGoal: number;
      payerGoal: number;
      revenueGoalAmount: number;
      revenueGoalCurrency: string;
    }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/goal`,
        data: {
          userGoal,
          payerGoal,
          revenueGoalAmount,
          revenueGoalCurrency,
        },
      }),

    GetGoal: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/goal`,
      }),

    GetPreviousData: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/goal/with-last-month-goal`,
      }),
    GetPaywallInfo: ({
      clientId,
      from,
      to,
      pageNumber,
      showRecords,
      search,
    }: {
      clientId: string;
      from: string;
      to: string;
      pageNumber: number;
      showRecords: number;
      search: string;
    }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/paywall/list-paywall-config`,
        params: {
          ...(clientId && { clientId }),
          ...(from && { from }),
          ...(to && { to }),
          ...(pageNumber && { pageNumber }),
          ...(showRecords && { showRecords }),
          ...(search && { search }),
        },
      }),
    GetPaywallInfoForImg: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/paywall/list-paywall-config`,
      }),
    GetPaywallJourneys: ({
      from,
      to,
      pageNumber,
      pageSize,
      search,
    }: {
      from: string;
      to: string;
      pageNumber: number;
      pageSize: number;
      search?: string;
    }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/journey/page`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
          ...(search && { search }),
          ...(pageNumber && { pageNumber }),
          showRecords: pageSize,
        },
      }),
    GetSingleUserJourneyPage: ({ id }: { id: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/journey/page/${id}`,
      }),
    CreateUserJourneyPage: ({ pageDataForPublish }: { pageDataForPublish: any }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/journey/page`,
        data: pageDataForPublish,
      }),
    UpdateUserJourneyPage: ({ pageData, id }: { pageData: any; id: string }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/journey/page/${id}`,
        data: pageData,
      }),
    DeleteUserJourneyPage: ({ id }: { id: string }) =>
      apiCaller({
        method: REST_METHODS.DELETE,
        url: `${API_URL}/journey/page/${id}`,
      }),

    CreateConditionForUserJourney: ({ conditionData }: { conditionData: any }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/journey/condition`,
        data: conditionData,
      }),
    UpdateConditionForUserJourney: ({ conditionData, id }: { conditionData: any; id: string }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/journey/condition/${id}`,
        data: conditionData,
      }),
    GetSegmentData: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/journey/segment`,
      }),
    CreateJourneySegment: ({ segmentData }: { segmentData: any }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/journey/segment`,
        data: segmentData,
      }),
    CalculateJourneySegment: ({ segmentData }: { segmentData: any }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/journey/segment/size`,
        data: segmentData,
      }),
    UpdateSegmentData: ({ id, segmentData }: { id: string; segmentData: any }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/journey/segment/${id}`,
        data: segmentData,
      }),
    DeleteSegmentData: ({ id }: { id: string }) =>
      apiCaller({
        method: REST_METHODS.DELETE,
        url: `${API_URL}/journey/segment/${id}`,
      }),
    AddCustomizationData: ({
      clientId,
      collectCustomizationdata,
    }: {
      clientId: string;
      collectCustomizationdata: any;
    }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/paywall/create-paywall-config?clientId=${clientId}`,
        data: collectCustomizationdata,
      }),
    UpdateCustomizationData: ({ collectCustomizationdata }: { collectCustomizationdata: any }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/paywall/update-paywall-config/${collectCustomizationdata.paywallId}`,
        data: collectCustomizationdata,
      }),

    GetTemplateInfo: ({
      paywallType,
      number_of_cta,
      displayType,
      deviceType,
    }: {
      paywallType: string;
      number_of_cta?: number | null;
      displayType?: string;
      deviceType?: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/paywall/list-paywall-template`,
        params: {
          ...(paywallType && { paywallType }),
          ...(number_of_cta && { number_of_cta }),
          ...(displayType && { displayType }),
          ...(deviceType && { deviceType }),
        },
      });
    },
    UpdatePaywallInfo: ({ paywallId, state }: { paywallId: string; state: boolean }) => {
      return apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/paywall/update-paywall-config/${paywallId}`,
        data: { enable: state },
      });
    },
    RevenueDataForOverview: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/revenue`,
      }),
    RevenueDataForExpanded: ({ from, to }: { from: string; to: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/expanded/revenue`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
        },
      }),
    PayersDataForOverview: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/payers`,
      }),
    PayersDataForExpanded: ({ from, to }: { from: string; to: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/expanded/payers`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
        },
      }),
    ActivePayingUsers: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/active-paying-users`,
      }),
    ActivePayingUsersExpanded: ({ from, to }: { from: string; to: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/expanded/active-paying-users`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
        },
      }),

    UserDataForOverview: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/users`,
      }),
    UserDataForExpanded: ({ from, to }: { from: string; to: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/expanded/users`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
        },
      }),
    cptForSummary: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/cpt`,
      }),
    rptForSummary: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/rpt`,
      }),
    topSelling: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/best-seller`,
      }),
    FiveOneR: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/five-oneR`,
      }),
    FiveOneDataExpanded: ({ from, to }: { from: string; to: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/expanded/five-oneR`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
        },
      }),

    RealTimeUser: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/real-time-user`,
      }),
    PremiumContent: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/premium-content`,
      }),

    TopContentsExpanded: ({ from, to, type, order }: { from: Date; to: Date; type: string; order: boolean }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/expanded/top-contents`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
          ...(type && { type }),
          ...(type && { order }),
        },
      }),

    TopContents: ({ from, to, type, order }: { from: Date; to: Date; type: string; order: boolean }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/top-contents`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
          ...(type && { type }),
          ...(type && { order }),
        },
      }),

    StopRateAnalysis: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/stop-rate-analysis`,
      }),

    ExpandedStopRate: ({ from, to }: { from: Date; to: Date }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/expanded/stop-rate-analysis`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
        },
      }),
    RevenueTrendnalysis: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/revenue-trend-analysis`,
      }),
    RevenueTrendnalysisExpanded: ({ from, to }: { from: string; to: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/expanded/revenue-trend-analysis`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
        },
      }),
    UserRetention: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/user-retention`,
      }),

    UserRetentionExpanded: ({ from, to }: { from: string; to: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/expanded/user-retention`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
        },
      }),

    TrafficSource: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/traffic-source`,
      }),
    AverageTransaction: ({ from, to, type }: { from: Date; to: Date; type: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/average-transaction`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
          ...(type && { type }),
        },
      }),
    UserChurn: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/user-churn`,
      }),
    SubscriptionChurn: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/subscription-churn`,
      }),
    RfmScore: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/analytics/rfm-score`,
      }),

    DeletePaywallInfo: ({ paywallId }: { paywallId: string }) => {
      return apiCaller({
        method: REST_METHODS.DELETE,
        url: `${API_URL}/paywall/paywall-config/${paywallId}`,
      });
    },
    CreateClient: ({
      name,
      address,
      pincode,
      email,
      password,
      gstNumber,
      panNumber,
      commission,
      internationalCommission,
      accountNumber,
      bankName,
      branchName,
      ifscCode,
      designation,
      pocName,
      contentDefaults,
      generateInvoices,
      iconUrl,
      taxType,
      hsn,
      buttonStyle,
      subscriptionText,
      subscriptionTitle,
      crossButtonClickUrl,
      homePageUrl,
      senderEmail,
      subscriptionDetails,
    }: {
      name: string;
      address: string;
      pincode: string;
      email: string;
      password: string;
      commission: number;
      internationalCommission: number;
      gstNumber: string;
      panNumber: string;
      accountNumber: string;
      bankName: string;
      branchName: string;
      ifscCode: string;
      designation: string;
      pocName: string;
      generateInvoices: boolean;
      iconUrl: string;
      taxType: string;
      hsn: string;
      contentDefaults: {
        price: number;
        duration: number;
        currency: string;
        contentType: string;
        priceOverrides?: {
          country: {
            name: string;
            price: number;
            currency: string;
          }[];
        };
      };
      buttonStyle?: any;
      subscriptionText?: string;
      subscriptionTitle?: string;
      crossButtonClickUrl?: string;
      homePageUrl: string;
      senderEmail?: string;
      subscriptionDetails: {
        commission: {
          physical: number;
          digital: number;
        };
        internationalCommission: {
          physical: number;
          digital: number;
        };
      };
    }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/`,
        data: {
          name,
          address,
          pincode,
          email,
          password,
          gstNumber,
          panNumber,
          accountNumber,
          commission,
          internationalCommission,
          bankName,
          branchName,
          ifscCode,
          designation,
          pocName,
          contentDefaults,
          generateInvoices,
          iconUrl,
          taxType,
          hsn,
          buttonStyle,
          paywallCustomization: {
            subscriptionText,
            subscriptionTitle,
            crossButtonClickUrl,
          },
          subscriptionDetails,
          homePageUrl,
          senderEmail,
        },
      }),
    UpdatePrice: ({
      price,
      duration,
      currency,
      contentType,
      priceOverrides,
    }: {
      price: number;
      duration: number;
      currency: string;
      contentType: string;
      priceOverrides?: {
        country: {
          name: string;
          price: number;
          currency: string;
        }[];
      };
    }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/update-content-defaults`,
        data: {
          price,
          duration,
          currency,
          contentType,
          priceOverrides,
        },
      }),

    UpdatePaywallCustomization: ({ formData }: { formData: FormData }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/update-paywall-customization`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }),
    uploadPaywallCustomizationImage: ({ paywallId, formData }: { paywallId: string; formData: FormData }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/paywall/upload-paywall-preview/${paywallId}`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }),
    paywallConfig: ({ paywallId, clientid }: { paywallId: string; clientid: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/paywall/paywall-config/${clientid}`,
        params: {
          paywallId: paywallId,
        },
      }),
    transactionRevenueData: ({ from, type, clientId }: { from: number; type: string; clientId?: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/transaction-revenue-data`,
        params: {
          ...(from && { from }),
          ...(type && { type }),
          ...(clientId && { clientId }),
        },
      }),
    FilterTransactionsData: ({
      status,
      skip,
      from,
      to,
      id,
      category,
      currency,
      phoneNumberOrEmail,
      download,
      clientId,
    }: transactionWithPagination) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/transaction-data/filter`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
          ...(status && { status }),
          ...(skip && { skip }),
          ...(id && { id }),
          ...(category && { category }),
          ...(currency && { currency }),
          ...(phoneNumberOrEmail && { phoneNumberOrEmail }),
          ...(download && { download }),
          ...(clientId && { clientId }),
        },
      }),
    TransactionsDataById: ({ id }: { id: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/transaction/${id}`,
      }),
    UpdateInternalUsers: ({ internalUsers }: { internalUsers: string }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/internal-user`,
        data: {
          internalUsers,
        },
      }),
    GetInternalUsers: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/internal-user`,
      }),
    UpdateWebhook: ({ enabled, url, type }: { enabled: boolean; url: string; type: string }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/webhook`,
        data: {
          enabled,
          url,
          type,
        },
      }),
    FetchWebhooks: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/webhooks`,
      }),
    PaywallCustomization: ({ clientId }: { clientId?: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/paywall-customization`,
        params: {
          ...(clientId && { clientId }),
        },
      }),
    EditClient: ({
      name,
      address,
      pincode,
      email,
      gstNumber,
      panNumber,
      accountNumber,
      bankName,
      branchName,
      commission,
      internationalCommission,
      ifscCode,
      designation,
      pocName,
      buttonStyle,
      subscriptionText,
      subscriptionTitle,
      crossButtonClickUrl,
      contentDefaults,
      generateInvoices,
      iconUrl,
      taxType,
      hsn,
      subscriptionDetails,
      enableMicroPricing,
      homePageUrl,
      senderEmail,
    }: {
      name: string;
      address: string;
      pincode: string;
      email: string;
      gstNumber: string;
      panNumber: string;
      accountNumber: string;
      bankName: string;
      branchName: string;
      ifscCode: string;
      designation: string;
      pocName: string;
      buttonStyle: any;
      subscriptionText: string;
      subscriptionTitle: string;
      generateInvoices: boolean;
      iconUrl: string;
      taxType: string;
      hsn: string;
      enableMicroPricing?: boolean;
      commission: number;
      internationalCommission: number;
      contentDefaults: {
        price: number;
        duration: number;
        currency: string;
        contentType: string;
        priceOverrides?: {
          country: {
            name: string;
            price: number;
            currency: string;
          }[];
        };
      };
      crossButtonClickUrl: string;
      subscriptionDetails: {
        commission: {
          physical: number;
          digital: number;
        };
        internationalCommission: {
          physical: number;
          digital: number;
        };
        pageMessage: string;
        subscriptionEnabled: boolean;
      };
      homePageUrl: string;
      senderEmail?: string;
    }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/edit`,
        data: {
          name,
          address,
          pincode,
          email,
          gstNumber,
          panNumber,
          accountNumber,
          commission,
          internationalCommission,
          bankName,
          branchName,
          ifscCode,
          designation,
          pocName,
          buttonStyle,
          generateInvoices,
          iconUrl,
          paywallCustomization: {
            subscriptionText,
            subscriptionTitle,
            crossButtonClickUrl,
          },
          contentDefaults,
          taxType,
          hsn,
          subscriptionDetails,
          enableMicroPricing,
          homePageUrl,
          senderEmail,
        },
      }),
    UpdatePassword: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/update-password`,
        data: {
          currentPassword,
          newPassword,
        },
      }),
    ResetPassword: ({ clientId, password }: { clientId: string; password: string }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/reset-password`,
        data: {
          clientId,
          password,
        },
      }),
    ClientDetails: ({ clientId }: { clientId?: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client`,
        params: {
          ...(clientId && { clientId }),
        },
      }),

    ClientLogo: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/logos`,
      }),

    GetPasses: ({ clientId }: { clientId?: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/passes`,
        params: {
          ...(clientId && { clientId }),
        },
      }),

    ClientNewUsersCsvs: ({ from, to }: Dates) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/new-users-csv`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
        },
      }),
    Invoices: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/invoices`,
      }),
    SubmitInvoice: ({ invoiceId }: { invoiceId: string }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/client/invoice/submit`,
        data: {
          invoiceId,
        },
      }),
    SetInvoiceStatus: ({ invoiceId, status, settledDate }: { invoiceId: string; status: string; settledDate?: Date }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/client/invoice/status`,
        data: {
          invoiceId,
          status,
          settledDate,
        },
      }),
    PendingInvoices: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/invoice?status=PENDING`,
      }),
    UserDetails: ({ userRecogniser }: { userRecogniser: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/user-details/${userRecogniser}`,
      }),
    UpdatePasses: ({
      passes,
      adFreeRedirectUrl,
      clientId,
    }: {
      passes: {
        price: number;
        currency: string;
        enabled: boolean;
        duration: number;
        title: string;
        priority: number;
        range?: {
          startDay: string;
          endDay: string;
          startTime: string;
          endTime: string;
        };
        priceOverrides?: {
          country: {
            name: string;
            price: number;
            currency: string;
          }[];
        };
      }[];
      adFreeRedirectUrl: string;
      clientId?: string;
    }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/client/passes/`,
        data: { passes, adFreeRedirectUrl, clientId },
      }),
    GetSubscriptionSettings: ({ clientId }: { clientId: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/settings`,
        params: {
          ...(clientId && { clientId }),
        },
      }),
    RedirectUserToContent: ({ clientId, clientContentId }: { clientId: string; clientContentId: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/redirect-to-content`,
        params: {
          ...(clientId && { clientId }),
          ...(clientContentId && { clientContentId }),
        },
      }),
    UpdateSubscriptionSettings: ({ formData }: { formData: FormData }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/subscription/update-settings`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }),
    RemoveImageFromSubscriptionSettings: ({ imageType }: { imageType: string }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/subscription/remove-image`,
        data: {
          imageType,
        },
      }),
    CreatePopUp: ({ formData }: { formData: FormData }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/popup`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }),
    PopUpDetails: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/popups`,
      }),
    UserJourneyDetails: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/user-journeys`,
      }),
    CohortDetails: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/cohorts`,
      }),
    CreateCohort: (cohort: {
      enabled: boolean;
      segments: { type: string; operator: string; value?: (string | number)[] }[];
      title: string;
    }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/cohort`,
        data: cohort,
      }),
    EditCohort: (cohort: {
      _id: string;
      enabled: boolean;
      segments: { type: string; operator: string; value?: (string | number)[] }[];
      title: string;
    }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/client/cohort/${cohort._id}`,
        data: cohort,
      }),
    AddStoryForRecommendation: ({ formData }: { formData: FormData }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/recommended-content`,
        data: formData,
      }),
    EditStoryForRecommendation: ({ formData, id }: { formData?: FormData; id: string }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/client/recommended-content/${id}`,
        data: formData,
      }),

    RecommendedContent: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/recommended-content`,
      }),
    DeleteStory: ({ id }: { id: string }) =>
      apiCaller({
        method: REST_METHODS.DELETE,
        url: `${API_URL}/client/recommended-content/${id}`,
      }),

    AddUserJourney: (userJourney: {
      enabled: boolean;
      title: string;
      activationConditions: { condition: string; value: string }[];
      cohorts: string[];
      result: {
        action: string;
        value: string;
      }[];
      _id: undefined;
      activationFrequency: string;
    }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/user-journey`,
        data: userJourney,
      }),
    EditUserJourney: (userJourney: {
      enabled: boolean;
      title: string;
      _id: string;
      activationConditions: { condition: string; value: string }[];
      cohorts: string[];
      result: {
        action: string;
        value: string;
      }[];
      activationFrequency: string;
    }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/client/user-journey/${userJourney._id}`,
        data: userJourney,
      }),
    GetRecommendationSettings: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/recommendation-settings`,
      }),
    ReloadAutomatedRecommendationsImages: () =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/recommendations/reload-images`,
      }),
    UpdateRecommendationSettings: ({
      desktopSettings,
      mobileSettings,
      manualEnabled,
      automatedEnabled,
      window,
      automaticDisplayCount,
      contentDisplayType,
    }: {
      manualEnabled?: boolean;
      automatedEnabled?: boolean;
      window?: number;
      automaticDisplayCount?: number;
      contentDisplayType?: string;
      desktopSettings?: {
        displayTitle: boolean;
        displayThumbnail: boolean;
        autoScroll: boolean;
        title: string;
        variant: string;
        variantPosition: string;
      };
      mobileSettings?: {
        displayTitle: boolean;
        displayThumbnail: boolean;
        autoScroll: boolean;
        title: string;
        variant: string;
        variantPosition: string;
      };
    }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/recommendation-settings`,
        data: {
          manualEnabled,
          automatedEnabled,
          window,
          automaticDisplayCount,
          desktopSettings,
          mobileSettings,
          contentDisplayType,
        },
      }),

    GetSubscriptions: ({ clientId, migrated }: { clientId?: string; migrated?: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription`,
        params: {
          ...(clientId && { clientId }),
          ...(migrated && { migrated }),
        },
      }),
    bundledSubscriptionSettings: ({ formData }: { formData: FormData }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/subscription/bundled-subscription-settings`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }),

    GetBundledSubscriptionSettings: ({ clientId }: { clientId?: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/bundled-subscription-settings`,
        params: {
          ...(clientId && { clientId }),
        },
      }),
    GetOffers: ({ clientId }: { clientId: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/offers`,
        params: {
          ...(clientId && { clientId }),
        },
      }),
    CreateOffer: ({ formData }: { formData: FormData }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/subscription/offer`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }),
    EditOffer: ({ formData, offerId }: { formData: FormData; offerId: string }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/subscription/offer/${offerId}`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }),
    CreateSubscription: ({ formData }: { formData: FormData }) =>
      apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/subscription`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }),

    EditSubscription: ({ formData, subscriptionId }: { formData: FormData; subscriptionId?: string }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/subscription/${subscriptionId}`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }),

    EditPopUp: ({ formData, popUpId }: { formData: FormData; popUpId?: string }) =>
      apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/client/popup/${popUpId}`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }),

    DownloadCoupons: ({ subscriptionId }: { subscriptionId: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/coupon-codes`,
        params: {
          ...(subscriptionId && { subscriptionId }),
        },
      }),
    stats: {
      ItemizedTransactions: ({
        contentIds,
        from,
        to,
        pageNumber,
        pageSize,
        clientId,
        download,
        type,
      }: paginationWithClientParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/itemized-transactions?${qs.stringify({
            contentIds,
            from,
            to,
            pageNumber,
            pageSize,
            clientId,
            download,
            ...(type && { type }),
          })}`,
        }),

      PurchasesByCountry: ({ from, to, purchaseType }: GeoLocationParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/purchases-by-country`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            ...(purchaseType && { purchaseType }),
          },
        }),
      UTMParameterStats: ({
        contentIds,
        from,
        to,
        utmParameter,
        clientId,
        subscriptionIds,
        onlySubscriptions,
        purchasesOnly,
        utmValue,
      }: UTMParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/utm-parameter-stats?${qs.stringify({
            contentIds,
            from,
            to,
            utmParameter,
            clientId,
            subscriptionIds,
            onlySubscriptions,
            purchasesOnly,
            utmValue,
          })}`,
        }),
      PassStats: ({ from, to, slice, clientId }: ConcurrentReadersParams) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/purchased-passes`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            ...(slice && { slice }),
            ...(clientId && { clientId }),
          },
        });
      },
      NewUsers: ({ from, to }: Dates) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/new-users`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        });
      },
      Payments: ({ from, to, promotional }: ClientPaymentStats) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/payments`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            ...(promotional && { promotional }),
          },
        });
      },
      UsersByCountry: ({ from, to }: BifurcatedPricesParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/users-by-country`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      Aggregated: ({ from, to, columns, slice }: AggregatedConsumptionParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/aggregated`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            columns,
            ...(slice && { slice }),
          },
        }),
      TopContents: ({ from, to, pageNumber, pageSize, clientId }: paginationWithClientParams, sortBy: string) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/top-contents`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            sortBy,
            ...(pageNumber && { pageNumber }),
            ...(pageSize && { pageSize }),
            ...(clientId && { clientId }),
          },
        }),
      TopUsers: ({ from, to, clientId }: BifurcatedPricesParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/top-users`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            ...(clientId && { clientId }),
          },
        }),
      ConcurrentReaders: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/concurrent-readers`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      ViewsByOS: () =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/users/os`,
        }),

      ContentStats: ({ contentIds, from, to, pageNumber, pageSize, clientId }: paginationWithClientParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/content/stats?${qs.stringify({
            contentIds,
            from,
            to,
            pageNumber,
            pageSize,
            clientId,
          })}`,
        }),
      TagsStats: ({ contentIds, from, to, pageNumber, pageSize, clientId }: paginationWithClientParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/tags/purchases?${qs.stringify({
            contentIds,
            from,
            to,
            pageNumber,
            pageSize,
            clientId,
          })}`,
        }),
      SubscriberStats: ({
        from,
        to,
        clientId,
        subscriptionId,
        download,
        type,
      }: {
        from?: Date;
        to?: Date;
        clientId?: string;
        subscriptionId?: string;
        download?: boolean;
        type?: string;
      }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/subscribers?${qs.stringify({
            from,
            to,
            clientId,
            subscriptionId,
            download,
            type,
          })}`,
        }),

      subscriptionStats: ({
        from,
        to,
        slice,
        subscriptionId,
      }: {
        from?: Date;
        to?: Date;
        slice?: string;
        subscriptionId?: string;
      }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/subscription/stats/aggregated?${qs.stringify({
            from,
            to,
            slice,
            subscriptionId,
          })}`,
        }),
      userStatistics: ({ timeRange }: { timeRange: string }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/users?${qs.stringify({
            timeRange,
          })}`,
        }),

      ConversionStatistics: ({ timeRange }: { timeRange: string }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/conversion-graph?${qs.stringify({
            timeRange,
          })}`,
        }),
      RevenueAndGMVStatistics: ({ from }: { from: Date }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/revenue`,
          params: {
            ...(from && { from }),
          },
        }),
      viewsByDevice: () =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/users/device`,
        }),
      UserActivity: () =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/user-activity`,
        }),
      purchaseStatistics: ({ timeRange }: { timeRange: string }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/purchases`,
          params: {
            ...(timeRange && { timeRange }),
          },
        }),
      payerStatistics: ({ timeRange }: { timeRange: string }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/payers`,
          params: {
            ...(timeRange && { timeRange }),
          },
        }),
      BifurcatedPrices: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/bifurcated-transactions`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      UserCounts: ({ clientId, from, to }: { clientId?: string; from?: Date; to?: Date }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/user-count`,
          params: {
            ...(clientId && { clientId }),
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      InactiveUsersDetails: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/inactive-users`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      UserDetails: () =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/user-details`,
        }),
      PayerDetails: () =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/payer-details`,
        }),
      PopupStats: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/popup-view`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
    },

    Feedback: ({ formData }: { formData: FormData }) => {
      return apiCaller({
        url: `${API_URL}/client/feedback`,
        method: REST_METHODS.POST,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      });
    },

    GetDownloadableContents: ({ clientId }: { clientId?: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/content/downloadable-content`,
        params: { clientId },
      });
    },

    AddNewMagazine: ({ formData }: { formData: FormData }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/content/add-pdf`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    EditMagazine: ({ formData, _id }: { formData: FormData; _id: string }) => {
      return apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/content/edit-pdf/${_id}`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },

    ViewPDF: ({ clientId, contentId }: { clientId?: string; contentId: string }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/content/view-pdf`,
        data: { clientId, contentId },
      });
    },

    AddNewLandingPage: ({ formData }: { formData: FormData }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/subscription/landing-page`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },

    GetLandingPages: ({ clientId }: { clientId?: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/landing-pages`,
        params: { clientId },
      });
    },

    EditLandingPage: ({ formData, _id }: { formData: FormData; _id: string }) => {
      return apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/subscription/landing-page/${_id}`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },

    CreateCampaign: ({
      pathURL,
      status,
      landingPageId,
      defaultCheck,
    }: {
      pathURL: string;
      status: boolean;
      landingPageId: string;
      defaultCheck: boolean;
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/subscription/campaign`,
        data: { pathURL, status, landingPageId, defaultCheck },
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },

    GetCampaigns: () => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/campaigns`,
      });
    },

    EditCampaign: ({
      pathURL,
      campaignId,
      status,
      defaultCheck,
      landingPageId,
    }: {
      pathURL?: string;
      campaignId: string;
      status?: boolean;
      defaultCheck?: boolean;
      landingPageId?: string;
    }) => {
      return apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/subscription/campaign/${campaignId}`,
        data: { pathURL, status, defaultCheck, landingPageId },
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },

    DeleteCampaign: ({ campaignId }: { campaignId: string }) => {
      return apiCaller({
        method: REST_METHODS.DELETE,
        url: `${API_URL}/subscription/campaign/${campaignId}`,
      });
    },

    SendEmailForVerification: ({ name, email }: { name?: string; email?: string }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/sendVerificationMail`,
        data: { name, email },
      });
    },
    ValidateClientEmailandMobile: ({ clientId }: { clientId: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/validateEmailAndMobile/${clientId}`,
      });
    },
    SendMobileOtp: ({ number, clientId }: { number: string; clientId?: string }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/sendMobileOtp`,
        data: { number, clientId },
      });
    },
    VerifyMobileOtp: ({ number, clientId, otp }: { number: string; clientId?: string; otp: string }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/verifyMobileOtp`,
        data: { number, clientId, otp },
      });
    },
    UpdateLoginCustomization: ({ postbody }: { postbody: any }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/client/update-login-customization`,
        data: postbody,
      });
    },
    GetLoginCustomization: ({ clientId }: { clientId?: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/client/login-customization`,
        params: {
          clientId,
        },
      });
    },
    UpdateLoginSettings: (formData: FormData) => {
      return apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/client/update-login-customization-settings`,
        data: formData,
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },
    UpdateLoginCustomizationSettings: ({
      uiConfig,
      page,
    }: {
      uiConfig: string;
      page: 'signIn' | 'userDetails' | 'otp';
    }) => {
      return apiCaller({
        method: REST_METHODS.PATCH,
        url: `${API_URL}/client/update-login-customization-ui`,
        data: {
          uiConfig,
          page,
        },
      });
    },

    CreateEmbeddedSubs: ({
      title,
      subscriptions,
    }: {
      title: string;
      subscriptions: {
        subscriptionId: string;
        priority: number;
        recommended: boolean;
      }[];
    }) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/subscription/embedded-subscription/`,
        data: { title, subscriptions },
      });
    },

    GetEmbeddedSubs: ({ clientId }: { clientId?: string }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/embedded-subscription/`,
        params: {
          ...(clientId && { clientId }),
        },
      });
    },

    EReader: {
      SetPageSettings: ({ formData }: { formData: FormData }) => {
        return apiCaller({
          method: REST_METHODS.POST,
          url: `${API_URL}/client/ereader`,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      },

      EditPageSettings: ({ formData }: { formData: FormData }) => {
        return apiCaller({
          method: REST_METHODS.PATCH,
          url: `${API_URL}/client/ereader`,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      },

      GetPageSettings: () => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/ereader`,
        });
      },

      SendMemberAccess: ({
        name,
        email,
        clientName,
        role,
      }: {
        name: string;
        email: string;
        clientName: string;
        role: string;
      }) => {
        return apiCaller({
          method: REST_METHODS.POST,
          url: `${API_URL}/client/ereader/member/access`,
          data: { name, email, clientName, role },
          headers: {
            'Content-Type': 'application/json',
          },
        });
      },

      GetMemberList: () => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/ereader/member/access/list`,
        });
      },

      MemberAccess: ({
        status,
        type,
        name,
        memberId,
      }: {
        status?: string;
        type?: string;
        name?: string;
        memberId: string;
      }) => {
        return apiCaller({
          method: REST_METHODS.PATCH,
          url: `${API_URL}/client/ereader/member/access`,
          data: { status, type, name, memberId },
          headers: {
            'Content-Type': 'application/json',
          },
        });
      },

      ResendMemberAccess: ({ email, clientName }: { email: string; clientName: string }) => {
        return apiCaller({
          method: REST_METHODS.POST,
          url: `${API_URL}/client/ereader/member/resend`,
          data: { email, clientName },
          headers: {
            'Content-Type': 'application/json',
          },
        });
      },

      GetListOfMembers: () => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/ereader/member/access/list`,
        });
      },

      SendAccessToMember: ({ members, type, id }: { members: any; type: string; id: string }) => {
        return apiCaller({
          method: REST_METHODS.POST,
          url: `${API_URL}/client/ereader/access`,
          data: { members, type, id },
          headers: {
            'Content-Type': 'application/json',
          },
        });
      },

      UpdateMemberName: ({ name, memberId }: { name: string; memberId: string }) => {
        return apiCaller({
          method: REST_METHODS.PATCH,
          url: `${API_URL}/client/ereader/member/access/`,
          data: { name, memberId },
          headers: {
            'Content-Type': 'application/json',
          },
        });
      },

      Login: ({
        email,
        password,
        type,
        clientId,
      }: {
        email: string;
        password: string;
        type?: string;
        clientId: string;
      }) => {
        return apiCaller({
          method: REST_METHODS.POST,
          url: `${API_URL}/member/login`,
          data: { email, password, type, clientId },
          headers: {
            'Content-Type': 'application/json',
          },
        });
      },

      ChangePassword: ({
        clientId,
        email,
        newPassword,
        oldPassword,
      }: {
        clientId: string;
        email: string;
        newPassword: string;
        oldPassword: string;
      }) => {
        return apiCaller({
          method: REST_METHODS.PATCH,
          url: `${API_URL}/member/updatePassword`,
          data: { clientId, email, newPassword, oldPassword },
          headers: {
            'Content-Type': 'application/json',
          },
        });
      },

      GetUploadList: ({ memberId, role }: { memberId: string; role: string }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/member/uploadList/${memberId}`,
          headers: {
            'Content-Type': 'application/json',
          },
          params: { role: role },
        });
      },

      UploadFile: ({ formData, domainUrl }: { formData: FormData; domainUrl: string }) => {
        return apiCaller({
          method: REST_METHODS.POST,
          url: `${domainUrl}/file`,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      },

      UpdateFile: ({ formData, domainUrl }: { formData: FormData; domainUrl: string }) => {
        return apiCaller({
          method: REST_METHODS.PATCH,
          url: `${domainUrl}/file`,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      },

      GetClientLogos: () => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/logos`,
        });
      },
      GetMemberUploadHistory: ({
        contentId,
        sortBy,
        status,
        role,
        type,
      }: {
        contentId: string | null;
        sortBy: string;
        status?: string;
        role: string;
        type?: string;
      }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/member/uploadHistory`,
          headers: {
            'Content-Type': 'application/json',
          },
          params: { filter: sortBy, status, contentId, role, type },
        });
      },

      DeleteFile: ({
        domainUrl,
        type,
        clientContentId,
        name,
        fileKey,
        coverPageKey,
        clientId,
      }: {
        type: string;
        clientContentId: string;
        domainUrl: string;
        name: string;
        fileKey: string;
        coverPageKey: string;
        clientId: string;
      }) => {
        return apiCaller({
          method: REST_METHODS.DELETE,
          url: `${domainUrl}/file`,
          data: { type, clientContentId, name, fileKey, coverPageKey, clientId },
        });
      },

      DeleteEdition: ({ removedEditions }: { removedEditions: any }) => {
        return apiCaller({
          method: REST_METHODS.DELETE,
          url: `${API_URL}/client/ereader/content/`,
          data: {
            content: [...removedEditions],
          },
        });
      },
    },
    Slp: {
      SlpStats: (startDate: any, endDate: any) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/slp-stats?start=${startDate}&end=${endDate}`,
        });
      },
      UtmSourceDropDownList: ({ start, end, landingPageId }: { start: string; end: string; landingPageId: string }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/slp-stats/landing-page-list/utm-and-source-dropdown-list`,
          params: {
            start,
            end,
            landingPageId,
          },
        });
      },
      UserViewTrends: ({
        startDate,
        endDate,
        type,
        segmentedBy,
      }: {
        startDate: any;
        endDate: any;
        type: any;
        segmentedBy: any;
      }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/slp-stats/user-view-trend?start=${startDate}&end=${endDate}&type=${type}&segmentedBy=${segmentedBy}`,
        });
      },
      LandingPageList: ({ startDate, endDate }: { startDate: string; endDate: string }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/slp-stats/landing-page-list/?start=${startDate}&end=${endDate}`,
        });
      },
      UtmSourceList: ({
        start,
        end,
        landingPageId,
        type,
        values,
        utm,
        sources,
      }: {
        start: string;
        end: string;
        type: string;
        landingPageId: string;
        values: string[];
        utm?: string;
        sources?: string[];
      }) => {
        return apiCaller({
          method: REST_METHODS.POST,
          url: `${API_URL}/analytics/slp-stats/landing-page-list/stats`,
          data: {
            start,
            end,
            landingPageId,
            type,
            values,
            utm,
            sources,
          },
        });
      },

      OtherPaywall: ({
        startDate,
        endDate,
        landingPageId,
        type,
      }: {
        startDate: string;
        endDate: string;
        type: string;
        landingPageId: string;
      }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/slp-stats/landing-page-list/source-type-list?start=${startDate}&end=${endDate}&landingPageId=${landingPageId}&type=${type}`,
        });
      },
      FlowAnalysis: ({ startDate, endDate, userType }: { startDate: any; endDate: any; userType: any }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/slp-stats/flow-analysis?start=${startDate}&end=${endDate}&userType=${userType}`,
        });
      },
      ExtendedFlowAnalysis: ({
        start,
        end,
        userType,
        landingPageId,
        tiers,
      }: {
        start: any;
        end: any;
        userType: any;
        landingPageId: string;
        tiers: string[];
      }) => {
        return apiCaller({
          method: REST_METHODS.POST,
          url: `${API_URL}/analytics/slp-stats/landing-page-list/extended-flow-analysis`,
          data: {
            start,
            end,
            userType,
            landingPageId,
            tiers,
          },
        });
      },
      GetTiersPerLandingPage: ({ start, end, landingPageId }: { start: any; end: any; landingPageId: string }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/slp-stats/landing-page-list/tier-ids-list`,
          params: {
            start,
            end,
            landingPageId,
          },
        });
      },
    },
    PaywallStats: {
      PaywallAccumulatedStats: ({
        startDate,
        endDate,
        clientId,
      }: {
        startDate: string;
        endDate: string;
        clientId: any;
      }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/paywall-stats/accumulates?clientId=${clientId}&startDate=${startDate}&endDate=${endDate}`,
        });
      },
      PaywallUserViewTrends: ({
        start,
        end,
        type,
        segmentedBy,
        clientId,
      }: {
        start: string;
        end: string;
        type: any;
        segmentedBy: any;
        clientId: any;
      }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/paywall-stats/user-view-trend?type=${type}&segmentedBy=${segmentedBy}&start=${start}&end=${end}&clientId=${clientId}`,
        });
      },
      PaywallFlowAnalysis: ({
        start: start,
        end: end,
        userType,
        clientId,
      }: {
        start: string;
        end: string;
        userType: any;
        clientId: any;
      }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/paywall-stats/flow-analysis?start=${start}&end=${end}&userType=${userType}&clientId=${clientId}`,
        });
      },
      PaywallPageList: ({ startDate, endDate, clientId }: { startDate: string; endDate: string; clientId: any }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/paywall-stats/paywall-pages-list?startDate=${startDate}&endDate=${endDate}&clientId=${clientId}`,
        });
      },
      PaywallUtmSourceList: ({
        start,
        end,
        paywallId,
        type,
        values,
        utm,
        sources,
      }: {
        start: any;
        end: any;
        type: any;
        paywallId: any;
        values: any[];
        utm?: any;
        sources?: any[];
      }) => {
        return apiCaller({
          method: REST_METHODS.POST,
          url: `${API_URL}/analytics/paywall-stats/segmented-bar-chart`,
          data: {
            start,
            end,
            paywallId,
            type,
            values,
            utm,
            sources,
          },
        });
      },

      PaywallUtmSourceDropDownList: ({ start, end, paywallId }: { start: string; end: string; paywallId: string }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/paywall-stats/utm-and-sources-list`,
          params: {
            start,
            end,
            paywallId,
          },
        });
      },
      PaywallExtendedFlowAnalysis: ({
        start: start,
        end: end,
        userType,
        clientId,
        paywallId,
      }: {
        start: string;
        end: string;
        userType: any;
        clientId: any;
        paywallId: any;
      }) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/analytics/paywall-stats/flow-analysis?start=${start}&end=${end}&userType=${userType}&clientId=${clientId}&paywallId=${paywallId}`,
        });
      },
    },
  },

  admin: {
    UserDetails: ({ userRecogniser }: { userRecogniser: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/admin/user-details/${userRecogniser}`,
      }),
    GetClients: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/admin/clients`,
      }),

    UserPhoneNumbers: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/admin/stats/user-phonenumbers`,
      }),
    PayerDetails: () =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/admin/stats/payer-details`,
      }),
    FetchCoupons: (paginationParams: PaginationParams) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/content/coupons`,
        params: paginationArgsToParams(paginationParams),
      });
    },
    LoginAsClient: ({ clientId }: { clientId: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/admin/login-as-client`,
        params: {
          ...(clientId && { clientId }),
        },
      }),

    stats: {
      RevenueAndGMVStatistics: ({ from, clientId }: { from: string; clientId?: string }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/revenue`,
          params: {
            ...(from && { from }),
            ...(clientId && { clientId }),
          },
        }),
      ItemizedTransactions: ({
        contentIds,
        from,
        to,
        pageNumber,
        pageSize,
        clientId,
        download,
        type,
      }: paginationWithClientParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/itemized-transactions?${qs.stringify({
            contentIds,
            from,
            to,
            pageNumber,
            pageSize,
            clientId,
            download,
            ...(type && { type }),
          })}`,
        }),
      PurchaseStatistics: ({ timeRange, clientId }: { timeRange: string; clientId?: string }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/purchases?${qs.stringify({
            timeRange,
            clientId,
          })}`,
        }),
      payerStatistics: ({ timeRange, clientId }: { timeRange: string; clientId?: string }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/payers`,
          params: {
            ...(timeRange && { timeRange }),
            ...(clientId && { clientId }),
          },
        }),
      PurchasesByCountry: ({ from, to, clientId, purchaseType }: GeoLocationParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/purchases-by-country`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            ...(clientId && { clientId }),
            ...(purchaseType && { purchaseType }),
          },
        }),
      UsersByCountry: ({ from, to, clientId }: BifurcatedPricesParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/users-by-country`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            ...(clientId && { clientId }),
          },
        }),
      UTMParameterStats: ({
        contentIds,
        from,
        to,
        utmParameter,
        clientId,
        subscriptionIds,
        onlySubscriptions,
        purchasesOnly,
      }: UTMParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/utm-parameter-stats?${qs.stringify({
            contentIds,
            from,
            to,
            utmParameter,
            clientId,
            subscriptionIds,
            onlySubscriptions,
            purchasesOnly,
          })}`,
        }),
      CouponStats: ({ from, to, clientId }: BifurcatedPricesParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/coupon?${qs.stringify({
            from,
            to,
            clientId,
          })}`,
        }),
      TagsStats: ({ contentIds, from, to, pageNumber, pageSize, clientId }: paginationWithClientParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/tags/purchases?${qs.stringify({
            contentIds,
            from,
            to,
            pageNumber,
            pageSize,
            clientId,
          })}`,
        }),
      userStatistics: ({ timeRange, clientId }: { timeRange: string; clientId?: string }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/users?${qs.stringify({
            timeRange,
            clientId,
          })}`,
        }),
      SubscriberStats: ({ from, to, clientId, download, type }: paginationWithClientParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/subscribers?${qs.stringify({
            from,
            to,
            clientId,
            download,
            type,
          })}`,
        }),
      BifurcatedPrices: ({ from, to, clientId }: BifurcatedPricesParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/bifurcated-transactions`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            ...(clientId && { clientId }),
          },
        }),
      TotalUsers: ({ from, to, slice }: TotalUsersParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/user-count`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            ...(slice && { slice }),
          },
        }),
      TotalWalletAddition: ({ from, to, slice }: TotalWalletAdditionParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/wallet-addition`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            ...(slice && { slice }),
          },
        }),
      PassStats: ({ from, to, slice, clientId }: ConcurrentReadersParams) => {
        return apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/purchased-passes`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            ...(slice && { slice }),
            ...(clientId && { clientId }),
          },
        });
      },

      AggregatedConsumption: ({ from, to, slice, columns, clientId }: AggregatedConsumptionParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/aggregated`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            columns,
            ...(slice && { slice }),
            ...(clientId && { clientId }),
          },
        }),

      ConcurrentReaders: ({ from, to, slice, clientId }: ConcurrentReadersParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/concurrent-readers`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            ...(slice && { slice }),
            ...(clientId && { clientId }),
          },
        }),

      WalletBalance: () =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/wallet-balance`,
        }),
      TopStories: ({ from, to, sortBy, pageNumber, pageSize }: TopStoriesParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/top-contents`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            sortBy,
            ...(pageNumber && { pageNumber }),
            ...(pageSize && { pageSize }),
          },
        }),
      UserCountByBalance: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/user-balance`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      ActiveUsers: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/active-users`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      PreAggregatedUserData: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/aggregated-user-data`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      TopUsers: ({ from, to, clientId }: BifurcatedPricesParams) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/top-users`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
            ...(clientId && { clientId }),
          },
        }),
      viewsByDevice: ({ clientId }: { clientId: string }) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/client/stats/users/device`,
          params: {
            ...(clientId && { clientId }),
          },
        }),
      NewUserStats: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/new-user`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      InactiveUsersPhoneNumbers: ({ from, to }: Dates) =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/inactive-users-phonenumbers`,
          params: {
            ...(from && { from }),
            ...(to && { to }),
          },
        }),
      TransactionCsvsWithGst: () =>
        apiCaller({
          method: REST_METHODS.GET,
          url: `${API_URL}/admin/stats/monthly-transactions-with-gst`,
        }),
    },
  },
  notification: {
    getClientNotifications: (paginationParams: PaginationParams) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/notification/client`,
        params: paginationArgsToParams(paginationParams),
      });
    },
    markNotificationsAsRead: () => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/notification/client/mark-read`,
      });
    },
  },
  subscription: {
    SubscriptionDetails: ({ clientId, currency }: { clientId: string; currency?: currencyType }) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/convert-subscription-currency`,
        params: { clientId, currency },
      });
    },
    SubscriptionStats: ({ from, to, slice, clientId }: ConcurrentReadersParams) => {
      return apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/stats/aggregated`,
        params: {
          ...(from && { from }),
          ...(to && { to }),
          ...(slice && { slice }),
          ...(clientId && { clientId }),
        },
      });
    },
    SubscriptionAccessCheck: ({ clientId, sessionId }: { clientId: string; sessionId: string }) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/access`,
        params: {
          ...(clientId && { clientId }),
          ...(sessionId && { sessionId }),
        },
      }),
    ContentAndSubscriptionTableStats: ({ clientId, from, to, pageNumber, pageSize }: SubscriptionStatsParams) =>
      apiCaller({
        method: REST_METHODS.GET,
        url: `${API_URL}/subscription/stats/content-or-subscription-page`,
        params: {
          ...(clientId && { clientId }),
          ...(from && { from }),
          ...(to && { to }),
          ...(pageNumber && { pageNumber }),
          ...(pageSize && { pageSize }),
        },
      }),
  },
  analyst: {
    eventPost: (data: any) => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${process.env.REACT_APP_KAFKA}collect/event`,
        data: {
          topic: 'demoTopic',
          messageBody: [data],
        },
      });
    },
  },

  internal: {
    LogError: (error: Record<string, any>): Promise<AxiosResponse<unknown>> => {
      return apiCaller({
        method: REST_METHODS.POST,
        url: `${API_URL}/auth/log-error`,
        data: { severity: 'ERROR', message: 'frontend-log-error', ...error },
      });
    },
  },
};
export default API;
